import React, { useState, useEffect, useContext, useCallback, useRef } from 'react';
import { Outlet, useNavigate, useLocation, useMatch } from 'react-router-dom';

import eventBus from '../../../utils/eventBus';
import timeFast from '../../../assets/production-flow/time-fast.svg';
import movement from '../../../assets/stock/TableMovementHeader/refresh-active.svg';
import pcp from '../../../assets/production-flow/pcp.svg';
import filter from '../../../assets/stock/TableMovementHeader/filter.svg';
import { LeftSide, SectionName, SubHeaderContainer, TabsDiv, Tab, RightSide, OptionsButton, FilterWrapper } from '../../../components/styles/subheader-style';
import { FilterDiv, FilterImg, FilterText, Content, Select, NewOPButton } from './style';
import ModalContext from '../../../contexts/ModalContext';
import ModuleKanban from '../Pipelines/ModuleKanban';
import { Filter, Filter as LucideFilter, Filter as FilterIcon } from 'lucide-react';
import styled from 'styled-components';
import { usePersistedFilters } from '../../../hooks/usePersistedFilters';
import FilterMenu from '../../../components/SubHeaderFilter/FilterMenu';
import AppliedFiltersDisplay from '../../../components/SubHeaderFilter/AppliedFiltersDisplay';
import { Header, Filter as SubHeaderFilterType } from '../../../components/SubHeaderFilter/types';

type PCPViewType = 'table' | 'board';

const PRODUCTION_FASHION_BASE = '/panel/production-fashion';

export interface ProductionFashionOutletType {
  tabIndex: number;
  setTabIndex: React.Dispatch<React.SetStateAction<number>>;
  pcpView: PCPViewType;
  setPCPView: React.Dispatch<React.SetStateAction<PCPViewType>>;
  quantity: number;
  setQuantity: React.Dispatch<React.SetStateAction<number>>;
  refresh: boolean;
  setRefresh: React.Dispatch<React.SetStateAction<boolean>>;
  appliedFilters: SubHeaderFilterType[];
}

const TAB_INDEX_ORDEM_PRODUCAO = 0;
const TAB_INDEX_MOVEMENT = 1;
const TAB_INDEX_PCP = 2;
const TAB_INDEX_BOARD = 3;

export const productionFashionOrderHeaders: Header[] = [
  { key: 'codeOfOP', label: 'OP', type: 'string' },
  { key: 'fashionOrderId', label: 'Pedido', type: 'number' },
  { key: 'product', label: 'Produto', type: 'string' },
  { key: 'color', label: 'Cor', type: 'string' },
  { key: 'totalItems', label: 'Qtd. de itens', type: 'number' },
  { key: 'cutter', label: 'Cortador', type: 'string' },
  { key: 'dressmaker', label: 'Costureira', type: 'string' },
  { key: 'status', label: 'Status', type: 'string', selectOptions: [
    { value: 'Pendente', label: 'Pendente' },
    { value: 'Iniciado', label: 'Iniciado' },
    { value: 'Concluído', label: 'Concluído' }
  ]}
];

export const productionFashionMovementHeaders: Header[] = [
  { key: 'origin', label: 'Origem', type: 'string' },
  { key: 'rawMaterialName', label: 'Código', type: 'string' },
  { key: 'quantity', label: 'Movimento', type: 'string' },
  { key: 'oldTotalWeight', label: 'Peso anterior (kg)', type: 'number' },
  { key: 'newTotalWeight', label: 'Peso novo (kg)', type: 'number' },
  { key: 'oldLengthInMm', label: 'Comp. anterior', type: 'number' },
  { key: 'newLengthInMm', label: 'Comp. novo', type: 'number' },
  { key: 'oldQuantity', label: 'Qtd. anterior', type: 'number' },
  { key: 'newQuantity', label: 'Qtd. nova', type: 'number' },
  { key: 'status', label: 'Status', type: 'string', selectOptions: [
    { value: '410', label: 'Desfeito' },
    { value: 'abatido', label: 'Abatido' },
    { value: 'PENDING', label: 'Pendente' },
    { value: 'APPROVED', label: 'Aprovada' },
    { value: 'ORDERED', label: 'Encomendada' },
    { value: 'DELIVERED', label: 'Comprada' },
    { value: 'COMPLETED', label: 'Concluída' },
    { value: 'CANCELLED', label: 'Cancelada' }
  ]},
  { key: 'createdAt', label: 'Data', type: 'date' }
];

const ContentStartFashion = styled(Content)`
background-color: #f4f4f4;
overflow: auto;
`;

const tabs = [
  { path: PRODUCTION_FASHION_BASE, image: timeFast, name: 'Ordem de produção', index: TAB_INDEX_ORDEM_PRODUCAO },
  { path: `${PRODUCTION_FASHION_BASE}/movement`, image: movement, name: 'Movimentação', index: TAB_INDEX_MOVEMENT },
  { path: `${PRODUCTION_FASHION_BASE}/pcp`, image: pcp, name: 'PCP', index: TAB_INDEX_PCP },
  { path: `${PRODUCTION_FASHION_BASE}/board`, image: movement, name: 'Funil', index: TAB_INDEX_BOARD }
];


const ProductionFashionSubHeader: React.FC<{
  tabIndexActived: number;
  pcpView: PCPViewType;
  setPCPView: React.Dispatch<React.SetStateAction<PCPViewType>>;
  onAddOPClick: () => void;
  appliedFilters: SubHeaderFilterType[];
  onApplyFilter: (f: SubHeaderFilterType) => void;
}> = ({ tabIndexActived, pcpView, setPCPView, onAddOPClick, appliedFilters, onApplyFilter }) => {
const navigate = useNavigate();
const [isFilterMenuOpen, setIsFilterMenuOpen] = useState(false);
const filterButtonRef = useRef<HTMLDivElement | null>(null);

const isOrdemProducaoTab = tabIndexActived === TAB_INDEX_ORDEM_PRODUCAO;
const isMovementTab = tabIndexActived === TAB_INDEX_MOVEMENT;
const isPCPTab = tabIndexActived === TAB_INDEX_PCP;
const isBoardTab = tabIndexActived === TAB_INDEX_BOARD;
const { setModalActive, setModalSection } = useContext(ModalContext)

const addFashionOP = () => {
  setModalActive(true)
  setModalSection('addFashionOP')
}

return (
  <SubHeaderContainer>
    <LeftSide>
      <SectionName>
        Produção
      </SectionName>
      <TabsDiv>
        {tabs.map((tab, index) => (
          <Tab
            key={index}
            $isFirst={index === 0}
            $isLast={index === tabs.length - 1}
            $isActive={index === tabIndexActived}
            onClick={() => {
              if (index !== tabIndexActived) navigate(tab.path);
            }}
          >
            {index === TAB_INDEX_BOARD ? (
              <Filter size={12.8} style={{ marginRight: 7 }} />
            ) : (
              <img src={tab.image} alt={`${tab.path}-icon`} />
            )}
            {tab.name}
          </Tab>
        ))}
      </TabsDiv>
    </LeftSide>
    <RightSide>
      {(isOrdemProducaoTab || isMovementTab) && (
        <FilterWrapper style={{ marginRight: 8 }}>
          <OptionsButton ref={filterButtonRef} onClick={() => setIsFilterMenuOpen(true)}>
            <LucideFilter size={18} />
          </OptionsButton>
          {isFilterMenuOpen && (
            <FilterMenu
              headers={isOrdemProducaoTab ? productionFashionOrderHeaders : productionFashionMovementHeaders}
              onApplyFilter={(f) => {
                onApplyFilter(f);
                setIsFilterMenuOpen(false);
              }}
              onClose={() => setIsFilterMenuOpen(false)}
              anchorRef={filterButtonRef}
              filters={appliedFilters}
            />
          )}
        </FilterWrapper>
      )}


      {isPCPTab && (
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', whiteSpace: 'nowrap' }}>
          <span style={{ fontSize: '14px', fontWeight: 600, color: '#303030' }}>Visualizar por</span>
          <Select
            className="custom-select"
            value={pcpView}
            onChange={(e: React.ChangeEvent<HTMLSelectElement>) => setPCPView(e.target.value as PCPViewType)}
            style={{ width: '8rem', height: '2rem' }}
          >
            <option value="table">Tabela</option>
            <option value="board">Quadro</option>
          </Select>
        </div>
      )}

      {(isOrdemProducaoTab || isMovementTab) && (
        <NewOPButton onClick={onAddOPClick} style={{ marginLeft: 8 }}>
          + Adicionar OP
        </NewOPButton>
      )}
    </RightSide>
  </SubHeaderContainer>
);
};

const ProductionFashionContent = () => {
const location = useLocation();
const navigate = useNavigate();
const startMatch = useMatch('/panel/production-fashion/start/:orderId');
const createMatch = useMatch('/panel/production-fashion/create');
const boardMatch = useMatch('/panel/production-fashion/board');
const [tabIndex, setTabIndex] = useState<number>(TAB_INDEX_ORDEM_PRODUCAO);
const [pcpView, setPCPView] = useState<PCPViewType>('table');
const [quantity, setQuantity] = useState(50);
const [refresh, setRefresh] = useState(false);

const orderFiltersState = usePersistedFilters('production_fashion_order');
const movementFiltersState = usePersistedFilters('production_fashion_movement');

const activeFiltersState = tabIndex === TAB_INDEX_MOVEMENT ? movementFiltersState : orderFiltersState;

useEffect(() => {
  const bumpMovements = () => setRefresh((r) => !r);
  eventBus.on('refreshProductionTable', bumpMovements);
  return () => {
    eventBus.off('refreshProductionTable', bumpMovements);
  };
}, []);

const handleAddOPClick = useCallback(() => {
  navigate('/panel/production-fashion/create');
}, [navigate]);

useEffect(() => {
  const p = location.pathname;
  if (p.includes('/production-fashion/start/')) {
    return;
  }
  let next = TAB_INDEX_ORDEM_PRODUCAO;
  if (p.includes('/production-fashion/board')) {
    next = TAB_INDEX_BOARD;
  } else if (p.includes('/production-fashion/pcp')) {
    next = TAB_INDEX_PCP;
  } else if (p.includes('/production-fashion/movement')) {
    next = TAB_INDEX_MOVEMENT;
  } else if (/\/production-fashion\/?$/.test(p)) {
    next = TAB_INDEX_ORDEM_PRODUCAO;
  }
  setTabIndex(next);
}, [location.pathname]);

const outletContext: ProductionFashionOutletType = {
  tabIndex,
  setTabIndex,
  pcpView,
  setPCPView,
  quantity,
  setQuantity,
  refresh,
  setRefresh,
  appliedFilters: activeFiltersState.appliedFilters
};

if (startMatch || createMatch) {
  return (
    <ContentStartFashion>
      <Outlet context={outletContext} />
    </ContentStartFashion>
  );
}

if (boardMatch) {
  return (
    <>
      <ModuleKanban
        moduleType="production"
        moduleName="Produção"
        subModule="fashion"
        headerLeftContent={
          <>
            <SectionName>Produção</SectionName>
            <TabsDiv>
              {tabs.map((tab, index) => (
                <Tab
                  key={index}
                  $isFirst={index === 0}
                  $isLast={index === tabs.length - 1}
                  $isActive={index === tabIndex}
                  onClick={() => {
                    if (index !== tabIndex) navigate(tab.path);
                  }}
                >
                  {index === TAB_INDEX_BOARD ? (
                    <LucideFilter size={12.8} style={{ marginRight: 7 }} />
                  ) : (
                    <img src={tab.image} alt={`${tab.path}-icon`} />
                  )}
                  {tab.name}
                </Tab>
              ))}
            </TabsDiv>
          </>
        }
      />
    </>
  );
}

return (
  <>
    <ProductionFashionSubHeader
      tabIndexActived={tabIndex}
      pcpView={pcpView}
      setPCPView={setPCPView}
      onAddOPClick={handleAddOPClick}
      appliedFilters={activeFiltersState.appliedFilters}
      onApplyFilter={activeFiltersState.handleApplyFilter}
    />
    {(tabIndex === TAB_INDEX_ORDEM_PRODUCAO || tabIndex === TAB_INDEX_MOVEMENT) && activeFiltersState.appliedFilters.length > 0 && (
      <AppliedFiltersDisplay
        filters={activeFiltersState.appliedFilters}
        onRemoveFilter={activeFiltersState.handleRemoveFilter}
        onClearAll={activeFiltersState.handleClearAll}
      />
    )}
    <Content>
      <Outlet context={outletContext} />
    </Content>
  </>
);
};

export default ProductionFashionContent;